import { useMemo } from 'react';

import { type Client, useClient } from 'urql';
import { fromPromise } from 'xstate';

import { CostChannel } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

import { type FetchUpsellsParams, type FetchUpsellsResponse } from '../../machine';
import { orderingMachineLog } from '../../machine/ordering-machine.logger';
import {
  type OrderChannel,
  PERSONALIZED_UPSELL_FLAG,
  type UpsellItem,
  type UpsellPersonalization,
} from '../../machine/ordering-machine.types';
import {
  BagUpsellItemsDocument,
  type BagUpsellItemsQuery,
  type BagUpsellItemsQueryVariables,
} from './graphql/BagUpsellItems.generated';
import {
  PersonalizedUpsellsDocument,
  type PersonalizedUpsellsQuery,
  type PersonalizedUpsellsQueryVariables,
} from './graphql/PersonalizedUpsellItems.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchBagUpsells = () => {
  const client = useClient();
  const { getFeatureFlagValue, waitForInitialized } = useTelemetry();

  const fetchUpsells = useMemo(
    () =>
      fromPromise<FetchUpsellsResponse, FetchUpsellsParams>(async ({ input }) => {
        const { restaurantId, orderChannel } = input;

        if (!restaurantId) return { upsellItems: [] };

        const variables = { restaurantId, costChannel: getCostChannel(orderChannel) };

        const upsellPersonalization = waitForInitialized().then(() =>
          getFeatureFlagValue<UpsellPersonalization>(PERSONALIZED_UPSELL_FLAG, 'control'),
        );

        const [personalizedItems, categoryItems] = await Promise.all([
          upsellPersonalization.then((variant) =>
            variant === 'treatment'
              ? fetchPersonalizedUpsells(client, variables).catch(logPersonalizedUpsellsError)
              : [],
          ),
          fetchCategoryUpsells(client, variables),
        ]);

        return { upsellItems: mergeUpsellItems(personalizedItems, categoryItems) };
      }),
    [client, getFeatureFlagValue, waitForInitialized],
  );

  return { fetchUpsells };
};

// ─── Queries ─────────────────────────────────────────────────────────────────

async function fetchCategoryUpsells(
  client: Client,
  variables: BagUpsellItemsQueryVariables,
): Promise<UpsellItem[]> {
  const response = await client
    .query<BagUpsellItemsQuery, BagUpsellItemsQueryVariables>(BagUpsellItemsDocument, variables, {
      requestPolicy: 'network-only',
    })
    .toPromise();

  if (response.error) {
    throw new Error('Failed to fetch category upsells'); // caught in xstate
  }

  return mapCategoryUpsellItems(response.data?.categoryUpsells);
}

async function fetchPersonalizedUpsells(
  client: Client,
  variables: PersonalizedUpsellsQueryVariables,
): Promise<UpsellItem[]> {
  const response = await client
    .query<PersonalizedUpsellsQuery, PersonalizedUpsellsQueryVariables>(
      PersonalizedUpsellsDocument,
      variables,
      { requestPolicy: 'network-only' },
    )
    .toPromise();

  if (response.error) {
    return logPersonalizedUpsellsError(response.error);
  }

  return mapPersonalizedUpsellItems(response.data?.personalizedUpsells);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function logPersonalizedUpsellsError(error: unknown): UpsellItem[] {
  orderingMachineLog.error('Failed to fetch personalized upsells', error);

  return [];
}

function getCostChannel(orderChannel: OrderChannel) {
  return orderChannel === 'delivery'
    ? CostChannel.DeliveryCostChannel
    : CostChannel.DefaultCostChannel;
}

function mergeUpsellItems(
  personalizedItems: UpsellItem[],
  categoryItems: UpsellItem[],
): UpsellItem[] {
  return [...personalizedItems, ...categoryItems].filter(
    (item, index, items) => items.findIndex((other) => other.id === item.id) === index,
  );
}

function mapCategoryUpsellItems(
  categoryUpsells: BagUpsellItemsQuery['categoryUpsells'] | undefined,
): UpsellItem[] {
  const firstCategory = categoryUpsells?.find((category) => category.categoryRanking === 1);

  if (!firstCategory) return [];

  return firstCategory.products.filter((product) => !product.outOfStock).map(toUpsellItem);
}

function mapPersonalizedUpsellItems(
  personalizedUpsells: PersonalizedUpsellsQuery['personalizedUpsells'] | undefined,
): UpsellItem[] {
  if (personalizedUpsells?.__typename !== 'PersonalizedUpsell') return [];

  return personalizedUpsells.products
    .filter((product) => !product.outOfStock)
    .map((product) => ({ ...toUpsellItem(product), isPersonalized: true }));
}

function toUpsellItem(product: UpsellProductFields): UpsellItem {
  return {
    id: product.id,
    name: product.name,
    cost: product.cost,
    channelCost: product.channelCost,
    imageUrl: product.imageUrl,
    outOfStock: product.outOfStock,
    menuId: product.menuId,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UpsellProductFields = {
  id: string;
  name: string;
  cost: number;
  channelCost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
};
